<template>
  <div>
    <label v-if="label.length > 0" class="text-gray-700 font-semibold text-base ml-2 mb-1 inline-block">{{ label }}</label>
    <calendar-picker
      v-bind="$attrs"
      @input="updateValue"
      :value="calendarData"
      :configs="calendarConfig"
      class="w-full"
      :newCurrentDate="currentDate"
      :placeholder="placeholder"
      :timezone="timezone"
    >
      <template #datePickerInput="props">
        <div class="w-full cursor-pointer bg-white border border-gray-200 rounded p-2 flex items-center h-10">
          <calendar-icon size="1.5x" class="text-gray-600" />
          <input
            :readonly="!props.isTypeable"
            type="text"
            class="w-full cursor-pointer text-base text-gray-700"
            :placeholder="placeholder.length > 0 ? placeholder : 'Date'"
            v-model="props.selectedDate"
          />
          <x-circle-icon v-if="props.selectedDate && clearable" size="1.5x" class="float-right text-gray-300" @click.stop="clearValue" />
        </div>
      </template>
      <template #dateRangeInputs="props">
        <div class="w-full cursor-pointer bg-white border border-gray-200 rounded p-2 flex justify-between items-center h-10">
          <calendar-icon size="1.5x" class="text-gray-600" />
          <div v-if="props.startDate && !props.endDate" class="text-base text-gray-700 font-normal">{{ props.startDate }}</div>
          <div v-else-if="props.startDate && props.endDate" class="text-base text-gray-700 font-normal">{{ props.startDate }} - {{ props.endDate }}</div>
          <div v-else class="text-base font-normal text-gray-400">{{ placeholder.length > 0 ? placeholder : 'Date Range' }}</div>
          <x-circle-icon v-if="props.startDate && clearable" size="1.5x" class="float-right text-gray-300" @click.stop="clearValue" />
          <input
            :readonly="!props.isTypeable"
            type="hidden"
            class="w-full cursor-pointer text-base text-gray-700"
            :placeholder="placeholder"
            v-model="props.startDate"
          />
          <input type="hidden" v-model="props.endDate" placeholder="" :readonly="!props.isTypeable" />
        </div>
      </template>
    </calendar-picker>
  </div>
</template>

<script>
import CalendarPicker from './Calendar/components/CalendarPicker.vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone.js'
import { CalendarIcon, XCircleIcon } from 'vue-feather-icons'
dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  components: {
    CalendarPicker,
    CalendarIcon,
    XCircleIcon
  },
  props: {
    /**
     * Current date object.
     * @model
     */
    value: {
      // default: new Date().toString()
    },
    /**
     * Change the type of date picker to display:
     * single - single date,
     * multiple - allows you to choose multiple dates in one picker.
     * range - allows you to choose a start and end date.
     */
    mode: {
      type: String,
      default: 'single'
    },
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * Optional label above the employee picker.
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Option to determine if selectedDate can be set to null.
     */
    clearable: {
      type: Boolean,
      default: false
    },
    /**
     * Timezone to use for dayjs
     */
    timezone: {
      type: String,
      default: dayjs.tz.guess()
    }
  },
  computed: {
    inputValue() {
      if (this.mode == 'range') {
        return {
          dateRange: { start: this.value.start, end: this.value.end }
        }
      } else if (this.mode == 'multiple') {
        let dates = []

        this.value.forEach((item) => {
          dates.push(item)
        })

        return dates
      } else {
        return {
          selectedDate: this.value.toString()
        }
      }
    },
    calendarData() {
      let calendarData = this.baseCalendarData

      if (this.mode == 'range') {
        if (this.value?.start && this.value?.end) {
          calendarData.dateRange.start = dayjs.tz(this.value.start, this.timezone).format('M/D/YYYY')
          calendarData.dateRange.end = dayjs.tz(this.value.end, this.timezone).format('M/D/YYYY')
        }
      } else if (this.mode == 'multiple') {
        calendarData.selectedDates = []

        this.value?.forEach((item) => {
          let dateObj = { date: '12/31/2000', dateTime: false, hour: '00', minute: '00' }
          dateObj.date = dayjs.tz(item.toString(), this.timezone).format('M/D/YYYY')
          calendarData.selectedDates.push(dateObj)
        })
      } else {
        if (this.value === null || this.value == '') {
        } else if (this.value && dayjs.tz(this.value, this.timezone).isValid()) {
          calendarData.selectedDate = dayjs.tz(this.value, this.timezone).format('M/D/YYYY')
        }
      }

      return calendarData
    },
    calendarConfig() {
      let calendarConfig = this.baseCalendarConfig

      if (this.mode == 'range') {
        calendarConfig.isDatePicker = false
        calendarConfig.isDateRange = true
      } else if (this.mode == 'multiple') {
        calendarConfig.isAutoCloseable = false
        calendarConfig.isDatePicker = false
        calendarConfig.isMultipleDatePicker = true
      }

      // return calendarConfig
      return { ...calendarConfig, ...this.$attrs }
    }
  },
  data() {
    return {
      currentDate: new Date(),
      baseCalendarData: {
        dateRange: { start: '', end: '' },
        multipleDateRange: [],
        selectedDate: false,
        selectedDateTime: false,
        selectedDates: [],
        selectedDatesItem: '',
        selectedHour: '00',
        selectedMinute: '00'
      },
      baseCalendarConfig: {
        sundayStart: true,
        dateFormat: 'mm/dd/yyyy',
        isModal: true,
        isAutoCloseable: true,
        isDatePicker: true,
        isDateRange: false,
        isMultipleDateRange: false,
        isMultipleDatePicker: false
      }
    }
  },
  methods: {
    clearValue() {
      switch (this.mode) {
        case 'range':
          let ts = { start: '', end: '' }
          this.baseCalendarData.dateRange = ts
          this.$emit('input', ts)
          break
        case 'single':
          this.baseCalendarData.selectedDate = null
          this.$emit('input', null)
          break
        default:
      }
    },
    updateValue(value) {
      let ts = null
      let start
      let end

      if (this.mode == 'range') {
        if (value.dateRange.start) {
          start = dayjs(value.dateRange.start).toISOString()
          this.currentDate = dayjs(value.dateRange.start).toDate()
        } else {
          this.currentDate = new Date()
        }

        if (value.dateRange.end) {
          end = dayjs(value.dateRange.end).toISOString()
        }

        ts = { start, end }
      } else if (this.mode == 'multiple') {
        let dates = []

        value.selectedDates.forEach((item) => {
          dates.push(dayjs(item.date).toISOString())
        })

        if (value.selectedDates.length > 0) {
          this.currentDate = dayjs(value.selectedDates[value.selectedDates.length - 1].date).toDate()
        } else {
          // this.currentDate = new Date()
        }

        ts = dates
      } else {
        if (value.selectedDate != null && value.selectedDate != '') {
          // conform odd formats to standard before we handle timezone
          // NOTE: I dont know why this is needed but formats like 2024/09/10 are not handled correctly
          value.selectedDate = dayjs(value.selectedDate).format('YYYY-MM-DD')
          // convert to UTC
          ts = dayjs.tz(value.selectedDate, this.timezone).toISOString()
          this.currentDate = dayjs(value.selectedDate).toDate()
        }
      }

      this.$emit('input', ts)
    }
  }
}
</script>

<style lang="postcss" scoped>
div >>> input {
  outline: none;
}
</style>
